import { useEffect, useState } from 'react';
import { del, get, post } from './api/client';
import { Button } from './components/ui';
import { readLocal, writeLocal } from './lib/storage';

let waiting: ServiceWorker | null = null;
let userAcceptedUpdate = false;
const updateListeners = new Set<() => void>();

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      const notify = (w: ServiceWorker | null) => {
        if (!w) return;
        waiting = w;
        updateListeners.forEach((l) => l());
      };
      if (reg.waiting && navigator.serviceWorker.controller) notify(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const w = reg.installing;
        w?.addEventListener('statechange', () => {
          if (w.state === 'installed' && navigator.serviceWorker.controller) notify(w);
        });
      });
      // Reload ONLY when the user accepted an update. The first install also fires
      // controllerchange (clients.claim) — reloading then would wipe what the user is typing.
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded || !userAcceptedUpdate) return;
        reloaded = true;
        window.location.reload();
      });
      setInterval(() => void reg.update().catch(() => undefined), 60 * 60_000);
    } catch {
      /* SW is an enhancement; the app works without it */
    }
  });
}

/** "New version available" banner — never reloads under the user without consent (drafts safe). */
export function UpdatePrompt() {
  const [show, setShow] = useState(!!waiting);
  useEffect(() => {
    const l = () => setShow(true);
    updateListeners.add(l);
    return () => {
      updateListeners.delete(l);
    };
  }, []);
  if (!show) return null;
  return (
    <div className="toasts" style={{ bottom: 'auto', top: 16 }}>
      <div className="toast info" role="status">
        <div className="toast-body">
          <strong>A new version of Task OS is available</strong>
          <span className="muted">Reload when you're ready — unsaved drafts are kept.</span>
        </div>
        <div className="toast-actions">
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              userAcceptedUpdate = true;
              waiting?.postMessage('SKIP_WAITING');
            }}
          >
            Reload
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShow(false)}>
            Later
          </Button>
        </div>
      </div>
    </div>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Install-to-home-screen prompt where the browser supports it. */
export function InstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => readLocal('taskos.install.dismissed', false));
  useEffect(() => {
    const fn = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', fn);
    return () => window.removeEventListener('beforeinstallprompt', fn);
  }, []);
  if (!evt || dismissed) return null;
  return (
    <div className="toasts" style={{ left: 16, right: 'auto' }}>
      <div className="toast info" role="status">
        <div className="toast-body">
          <strong>Install Task OS</strong>
          <span className="muted">Add it to your home screen for a full-screen app.</span>
        </div>
        <div className="toast-actions">
          <Button
            size="sm"
            variant="primary"
            onClick={async () => {
              await evt.prompt();
              setEvt(null);
            }}
          >
            Install
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              writeLocal('taskos.install.dismissed', true);
              setDismissed(true);
            }}
          >
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}

function urlB64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export type PushState = 'unsupported' | 'not-configured' | 'denied' | 'off' | 'on';

export async function pushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  const { publicKey } = await get<{ publicKey: string | null }>('/api/push/vapid');
  if (!publicKey) return 'not-configured';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

export async function enablePush(): Promise<PushState> {
  const { publicKey } = await get<{ publicKey: string | null }>('/api/push/vapid');
  if (!publicKey) return 'not-configured';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return 'denied';
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(publicKey) });
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await post('/api/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await del('/api/push/subscribe', { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe();
  }
  return 'off';
}
